import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from "recharts";
import createCustomTooltip from "../components/CustomTooltip";
import { useResultsContext } from "../context/context";

interface LineData {
  name: string;
  key: string;
  colour: string;
}

interface Props {
  data: object[];
  formatter?: (val: number) => string | number;
  toolTipFormatter?: (val: number) => string | number;
  yAxisFormatter?: (val: number) => string | number;
  maxYAxisvalue: number;
  minYAxisvalue: number;
  numberOfTicks: number;
  lines: LineData[];
  tooltipOrdering?: "highest" | "lowest";
}

const ResultsLineChart: React.FC<Props> = ({
  data,
  lines,
  formatter,
  yAxisFormatter,
  maxYAxisvalue,
  minYAxisvalue,
  numberOfTicks,
  tooltipOrdering = "highest",
  toolTipFormatter,
}) => {
  const context = useResultsContext();

  const [responsiveSettings, setResponsiveSettings] = useState({
    strokeWidth: 2,
    circleRadius: 5,
  });

  useEffect(() => {
    const updateResponsiveSettings = () => {
      let strokeWidth = 4;
      let circleRadius = 10;

      if (smallScreen.matches) {
        strokeWidth = 1.5;
        circleRadius = 3;
      } else if (mediumScreen.matches) {
        strokeWidth = 2.5;
        circleRadius = 6;
      }

      setResponsiveSettings({ strokeWidth, circleRadius });
    };

    const largeScreen = window.matchMedia("(max-width: 3000px)");
    const mediumScreen = window.matchMedia("(max-width: 2000px)");
    const smallScreen = window.matchMedia("(max-width: 820px)");

    largeScreen.addEventListener("change", updateResponsiveSettings);
    mediumScreen.addEventListener("change", updateResponsiveSettings);
    smallScreen.addEventListener("change", updateResponsiveSettings);

    updateResponsiveSettings();

    return () => {
      largeScreen.removeEventListener("change", updateResponsiveSettings);
      mediumScreen.removeEventListener("change", updateResponsiveSettings);
      smallScreen.removeEventListener("change", updateResponsiveSettings);
    };
  }, []);

  return (
    <>
      <ResponsiveContainer width="100%" height="92%" debounce={1}>
        <LineChart
          data={data}
          margin={{
            top: 20,
            right: 15,
            left: 15,
            bottom: 10,
          }}
        >
          <CartesianGrid vertical={false} />
          <ReferenceLine y={0} stroke="#000" />
          <XAxis
            dataKey="name"
            tickLine={false}
            padding={{ left: 75, right: 75 }}
            tick={{
              style: {
                fontSize: "clamp(0.15rem, calc(0.15rem + 0.60vw), 1.4rem)",
              },
            }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            minTickGap={0}
            tickMargin={0}
            tickCount={numberOfTicks + 1}
            tickFormatter={
              yAxisFormatter
                ? (tick: any) => yAxisFormatter(tick)
                : formatter
                  ? (tick) => formatter(tick)
                  : (tick) => tick
            }
            domain={[minYAxisvalue, maxYAxisvalue]}
            tick={{
              style: {
                fontSize: "clamp(0.15rem, calc(0.15rem + 0.60vw), 1.4rem)",
              },
            }}
          />
          <Tooltip
            formatter={
              toolTipFormatter
                ? (tick: any) => toolTipFormatter(tick)
                : formatter
                  ? (tick: any) => formatter(tick)
                  : (tick: any) => tick
            }
            content={createCustomTooltip(tooltipOrdering)}
          />
          {!context.hideTeamPositions && (
            <Legend
              wrapperStyle={{
                bottom: 10,
                left: 0,
                right: 0,
                fontSize: "clamp(0.15rem, calc(0.15rem + 0.65vw), 2rem)",
              }}
            />
          )}
          {lines.map((l, idx) => (
            <Line
              key={`line-item-${idx}`}
              name={l.name}
              type="natural"
              dataKey={l.key}
              stroke={l.colour}
              strokeWidth={responsiveSettings.strokeWidth}
              dot={{ r: responsiveSettings.circleRadius }}
              animationDuration={context.noAnimations ? 0 : 1000}
              animationBegin={context.noAnimations ? 0 : idx * 1000}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </>
  );
};

export default ResultsLineChart;
